/**
 * Alpine.js component: the top-bar notification bell.
 *
 * @param {Object} config  { baseUrl: '/notifications', intervalMs: 60000 }
 *
 * Portal proxies (all same-origin, cookie auth):
 *   GET  {baseUrl}/unreadcount      -> { count }
 *   GET  {baseUrl}/latest           -> { success, items, unread, total }
 *   POST {baseUrl}/{id}/read        -> { success }   (antiforgery header)
 *   POST {baseUrl}/readall          -> { success }
 *
 * The unread count is fetched on load and every intervalMs while the tab is visible; the list
 * only when the dropdown opens. Rows with a url are plain links: the detail page marks the
 * object's notifications read server-side, so the bell on the next page is already right.
 */
function notificationBell(config) {
    var intervalMs = (config && config.intervalMs) || 60000;
    var baseUrl = (config && config.baseUrl) || '/notifications';
    var headers = { 'X-Requested-With': 'fetch', 'Accept': 'application/json' };

    return {
        open: false,
        count: 0,
        items: [],
        loading: false,
        loaded: false,
        error: null,
        busy: false,
        timer: null,
        onVisible: null,

        init() {
            this.refreshCount();
            this.timer = setInterval(function () {
                if (document.visibilityState === 'visible') this.refreshCount();
            }.bind(this), intervalMs);
            this.onVisible = function () {
                if (document.visibilityState === 'visible') this.refreshCount();
            }.bind(this);
            document.addEventListener('visibilitychange', this.onVisible);
        },

        destroy() {
            this.stop();
            if (this.onVisible) document.removeEventListener('visibilitychange', this.onVisible);
        },

        stop() {
            if (this.timer) clearInterval(this.timer);
            this.timer = null;
        },

        // Background poll: never reloads the page under the user. When the session has expired
        // the proxy answers with a redirect to sign-in; go quiet until the next navigation.
        async refreshCount() {
            try {
                var r = await fetch(baseUrl + '/unreadcount', { credentials: 'same-origin', headers: headers });
                if (r.redirected && /\/signin/i.test(r.url)) { this.stop(); return; }
                if (!r.ok) return;
                var data = await r.json();
                if (data && typeof data.count === 'number') this.count = data.count;
            } catch (e) { /* the badge is a hint, stay silent */ }
        },

        toggle() {
            this.open = !this.open;
            if (this.open) this.loadLatest();
        },

        close() { this.open = false; },

        // User-initiated, so here a sign-in redirect does reload (the recentActivity precedent).
        async loadLatest() {
            if (this.loading) return;
            this.loading = true;
            this.error = null;
            try {
                var r = await fetch(baseUrl + '/latest', { credentials: 'same-origin', headers: headers });
                if (r.redirected && /\/signin/i.test(r.url)) { window.location.reload(); return; }
                if (!r.ok) throw new Error(await portalErrorMessage(r));
                var data = await r.json();
                if (data.success === false) throw new Error(data.error || 'Could not load notifications.');
                this.items = data.items || [];
                if (typeof data.unread === 'number') this.count = data.unread;
                this.loaded = true;
            } catch (e) {
                this.error = e && e.message ? e.message : 'Could not load notifications.';
            } finally {
                this.loading = false;
            }
        },

        async post(url) {
            var r = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'fetch',
                    'RequestVerificationToken': portalAntiForgeryToken()
                }
            });
            var data = await r.json().catch(function () { return {}; });
            if (!r.ok || data.success === false) throw new Error(data.error || 'Request failed (' + r.status + ').');
            return data;
        },

        async markRead(n) {
            if (!n || n.isRead || this.busy) return;
            this.busy = true;
            this.error = null;
            try {
                await this.post(baseUrl + '/' + n.id + '/read');
                n.isRead = true;
                this.count = Math.max(0, this.count - 1);
            } catch (e) {
                this.error = e.message;
            } finally {
                this.busy = false;
            }
        },

        async markAllRead() {
            if (this.busy) return;
            this.busy = true;
            this.error = null;
            try {
                await this.post(baseUrl + '/readall');
                this.items.forEach(function (i) { i.isRead = true; });
                this.count = 0;
            } catch (e) {
                this.error = e.message;
            } finally {
                this.busy = false;
            }
        }
    };
}
